/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import Toolbar from "@mui/material/Toolbar";
import React from "react";
import {
  getDrawerSxTransitionMixin,
  getDrawerWidthTransitionMixin,
} from "../../utils";
import { Box } from "../Box";
import { Drawer } from "../Drawer";
import { SidebarNavigationDrawerProps } from "./ISidebar";
import SidebarNavigation from "./SidebarNavigation";

const SidebarNavigationDrawer = ({
  sidebarExpandedWidth = 320,
  disableCollapsibleSidebar = false,
  isDesktopNavigationExpanded,
  isMobileNavigationExpanded,
  isNavigationExpanded,
  hasDrawerTransitions,
  isNavigationFullyExpanded,
  sidebarFooter,
  handleNavigationLinkClick,
  handleSetNavigationExpanded,
  selectedItemIdRef,
  layoutRef,
  navigation,
}: SidebarNavigationDrawerProps) => {
  const isDesktopMini = !disableCollapsibleSidebar && !isDesktopNavigationExpanded;
  const isMobileMini = !disableCollapsibleSidebar && !isMobileNavigationExpanded;

  const getDrawerSharedSx = React.useCallback(
    (isMini: boolean, isTemporary: boolean) => {
      const drawerWidth = isMini ? 64 : sidebarExpandedWidth;
      return {
        displayPrint: "none",
        width: drawerWidth,
        flexShrink: 0,
        ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        ...(isTemporary ? { position: "absolute" } : {}),
        [`& .MuiDrawer-paper`]: {
          position: "absolute",
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundImage: "none",
          ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        },
      };
    },
    [isNavigationExpanded, sidebarExpandedWidth],
  );

  const getDrawerContent = React.useCallback(
    (isMini: boolean, viewport: "phone" | "tablet" | "desktop") => (
      <>
        <Toolbar />
        <Box
          component="nav"
          aria-label={`${viewport.charAt(0).toUpperCase()}${viewport.slice(1)}`}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "auto",
            pt: navigation[0]?.kind === "header" && !isMini ? 0 : 2,
            ...(hasDrawerTransitions
              ? getDrawerSxTransitionMixin(isNavigationFullyExpanded, "padding")
              : {}),
          }}
        >
          <SidebarNavigation
            subNavigation={navigation}
            onLinkClick={handleNavigationLinkClick}
            isMini={isMini}
            isFullyExpanded={isNavigationFullyExpanded}
            hasDrawerTransitions={hasDrawerTransitions}
            selectedItemId={selectedItemIdRef.current}
          />
          {sidebarFooter ?? null}
        </Box>
      </>
    ),
    [
      sidebarFooter,
      handleNavigationLinkClick,
      hasDrawerTransitions,
      isNavigationFullyExpanded,
      navigation,
    ],
  );

  return (
    <>
      <Drawer
        container={layoutRef.current}
        variant="temporary"
        open={isMobileNavigationExpanded}
        onClose={() => handleSetNavigationExpanded(false)()}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: {
            xs: "block",
            sm: disableCollapsibleSidebar ? "block" : "none",
            md: "none",
          },
          ...getDrawerSharedSx(false, true),
        }}
      >
        {getDrawerContent(false, "phone")}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: {
            xs: "none",
            sm: disableCollapsibleSidebar ? "none" : "block",
            md: "none",
          },
          ...getDrawerSharedSx(isMobileMini, false),
        }}
      >
        {getDrawerContent(isMobileMini, "tablet")}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          ...getDrawerSharedSx(isDesktopMini, false),
        }}
      >
        {getDrawerContent(isDesktopMini, "desktop")}
      </Drawer>
    </>
  );
};

export default SidebarNavigationDrawer;
